import React, { useRef, useEffect } from 'react';
import { usePageEditMode } from '@/context/PageEditModeContext';

/**
 * Per-page inline-editable value.
 *
 * Reads/writes the current PageEditProvider's slug via dotted `path`
 * (e.g. `hero.title` or `pricing.0.price`).  When edit mode is off it
 * renders as a plain element with either the saved value or the passed
 * `fallback` (which should mirror the original hard-coded copy so first
 * render matches the current site).
 *
 * Props:
 *   path       dotted config path
 *   fallback   value shown when nothing saved yet
 *   as         HTML tag (default 'span')
 *   multiline  keep newlines when reading textContent
 *   className  passed through
 */
export const PageEditable = ({
  path,
  fallback = '',
  as = 'span',
  multiline = false,
  className = '',
  children,
  ...rest
}) => {
  const { enabled, getValue, update } = usePageEditMode();
  const ref = useRef(null);
  const Tag = as;

  const current = getValue(path);
  const value = current === undefined || current === null || current === ''
    ? (fallback || children || '')
    : current;

  useEffect(() => {
    if (enabled && ref.current && ref.current.textContent !== String(value ?? '')) {
      ref.current.textContent = String(value ?? '');
    }
  }, [enabled, value]);

  const stopIfEditing = (e) => {
    if (enabled) e.stopPropagation();
  };

  if (!enabled) {
    return <Tag className={className} {...rest}>{value}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm cursor-text focus:outline-primary focus:outline-solid`}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      onMouseDown={stopIfEditing}
      onClick={stopIfEditing}
      onPointerDown={stopIfEditing}
      onBlur={(e) => {
        const text = multiline ? e.currentTarget.innerText : e.currentTarget.textContent;
        update(path, text || '');
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      data-edit-path={path}
      {...rest}
    >
      {value}
    </Tag>
  );
};

export default PageEditable;
